import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { InviteFunnel } from "@/components/marketing/orderInvite/InviteFunnel";
import { InviteForecastMembers } from "@/components/marketing/orderInvite/InviteForecastMembers";
import { describeTodayBatch, formatNextBatch, type InviteForecast } from "@/utils/inviteForecast";
import { formatCurrency } from "@/utils/orderUtils";
import { orderWindowForRun } from "@/utils/smsShared";
import type { MarketingCampaign, MarketingSettings } from "@/types/sms";

interface HeroProps {
  forecast: InviteForecast | undefined;
  settings: MarketingSettings;
  /** Segments one invite costs, from the real template (never hardcoded 1). */
  segmentsPerInvite: number;
  paused: boolean;
  /** sms_invite_rotations.enabled. */
  enabled: boolean;
  /** Today's invite batch row, once the rotation has materialised it. */
  todayBatch: MarketingCampaign | null;
  /** Outbound invite texts still queued. */
  queuedCount: number;
  onOpenBatch: (batch: MarketingCampaign) => void;
}

/**
 * The "Today" hero (captain-approved round-4 design): today's batch cohort
 * (fixed all day: everyone who ordered yesterday, store-local), what happened
 * or will happen to it, the eligibility funnel, who is in it, and the
 * per-batch cost. The "This month" roll-up lives on the Contacts tab
 * (ContactsMonthCard). Read-only.
 */
export function OrderInviteHero({
  forecast,
  settings,
  segmentsPerInvite,
  paused,
  enabled,
  todayBatch,
  queuedCount,
  onOpenBatch,
}: HeroProps) {
  const tz = settings.timezone;
  const costPerInvite = segmentsPerInvite * settings.sms_cost_per_segment;
  const today = describeTodayBatch({
    eligible: forecast?.eligible_now ?? 0,
    todayBatchAt: forecast?.today_batch_at ?? null,
    enabled,
    paused,
    todayBatch,
    queuedCount,
  });
  const todayCost = today.count * costPerInvite;

  const todayBatchAt = forecast?.today_batch_at ?? null;
  const todayLabel = formatNextBatch(todayBatchAt, tz);
  const windowDayLabel = todayBatchAt
    ? new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: tz }).format(
        new Date(orderWindowForRun(new Date(todayBatchAt), tz).start),
      )
    : null;
  const sendTimeLabel = todayBatchAt
    ? new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", timeZone: tz }).format(new Date(todayBatchAt))
    : "";

  return (
    <Card>
      <CardContent className="space-y-3 p-4 sm:p-6">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Today</h3>
          <span className="text-xs text-muted-foreground">batch {todayLabel}</span>
        </div>
        <div className="flex items-baseline gap-2">
          <span
            className={`text-4xl font-bold tabular-nums ${today.sends && !paused ? "text-foreground" : "text-muted-foreground"}`}
          >
            {today.count}
          </span>
          <span className="text-sm text-muted-foreground">
            invite{today.count === 1 ? "" : "s"} {today.verb}
          </span>
        </div>
        {today.note && <p className="text-xs font-medium text-status-danger">{today.note}</p>}
        {windowDayLabel && (
          <p className="text-xs text-muted-foreground">Customers whose last order was yesterday, {windowDayLabel}.</p>
        )}

        {today.phase === "ran" && todayBatch ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              {todayBatch.recipients_count} customer{todayBatch.recipients_count === 1 ? "" : "s"} reviewed ·{" "}
              {todayBatch.sent_count} invited
            </p>
            <Button variant="outline" className="h-11" onClick={() => onOpenBatch(todayBatch)}>
              See who was in it
            </Button>
          </div>
        ) : forecast ? (
          <>
            <InviteFunnel funnel={forecast.funnel} />
            <InviteForecastMembers
              members={forecast.members}
              total={forecast.funnel.ordered_in_window}
              timezone={tz}
            />
          </>
        ) : (
          <p className="text-sm text-muted-foreground">Loading the forecast…</p>
        )}

        {forecast && forecast.next_so_far !== null && (
          <p className="text-xs text-muted-foreground">
            Next batch {formatNextBatch(forecast.next_batch_at, tz)}:{" "}
            <span className="font-medium text-foreground tabular-nums">{forecast.next_so_far}</span> so far, from
            orders since {sendTimeLabel} today.
          </p>
        )}

        <p className="border-t border-border pt-2 text-xs text-muted-foreground">
          {today.sends ? "Cost today" : "Would cost"}:{" "}
          <span className="font-medium text-foreground">{formatCurrency(todayCost)}</span> · {segmentsPerInvite} segment
          {segmentsPerInvite === 1 ? "" : "s"} × {formatCurrency(settings.sms_cost_per_segment)} each
        </p>
      </CardContent>
    </Card>
  );
}
